import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { authService, userProfileService } from '../../../services/supabaseService';
import VerseCompletionModal from './VerseCompletionModal';
import { AudioPlayerContext } from '../../../contexts/AudioPlayerContext';
import {
  loadChapterAudio,
  getNeural2Voices,
  getVerseMarksForVerse,
  buildChunkSteps,
  playSegment,
} from '../../../services/mp3MemorizationService';
import useTouchSwipe, { triggerHaptic } from '../../../hooks/useTouchSwipe';

// Book full-name → abbreviation map (needed to query audio service)
const BOOK_ABBREV_MAP = {
  'Genesis': 'GEN', 'Exodus': 'EXO', 'Leviticus': 'LEV', 'Numbers': 'NUM',
  'Deuteronomy': 'DEU', 'Joshua': 'JOS', 'Judges': 'JDG', 'Ruth': 'RUT',
  '1 Samuel': '1SA', '2 Samuel': '2SA', '1 Kings': '1KI', '2 Kings': '2KI',
  '1 Chronicles': '1CH', '2 Chronicles': '2CH', 'Ezra': 'EZR', 'Nehemiah': 'NEH',
  'Esther': 'EST', 'Job': 'JOB', 'Psalms': 'PSA', 'Proverbs': 'PRO',
  'Ecclesiastes': 'ECC', 'Song of Solomon': 'SNG', 'Isaiah': 'ISA',
  'Jeremiah': 'JER', 'Lamentations': 'LAM', 'Ezekiel': 'EZK', 'Daniel': 'DAN',
  'Hosea': 'HOS', 'Joel': 'JOE', 'Amos': 'AMO', 'Obadiah': 'OBA',
  'Jonah': 'JON', 'Micah': 'MIC', 'Nahum': 'NAH', 'Habakkuk': 'HAB',
  'Zephaniah': 'ZEP', 'Haggai': 'HAG', 'Zechariah': 'ZEC', 'Malachi': 'MAL',
  'Matthew': 'MAT', 'Mark': 'MRK', 'Luke': 'LUK', 'John': 'JHN',
  'Acts': 'ACT', 'Romans': 'ROM', '1 Corinthians': '1CO', '2 Corinthians': '2CO',
  'Galatians': 'GAL', 'Ephesians': 'EPH', 'Philippians': 'PHP', 'Colossians': 'COL',
  '1 Thessalonians': '1TH', '2 Thessalonians': '2TH', '1 Timothy': '1TI',
  '2 Timothy': '2TI', 'Titus': 'TIT', 'Philemon': 'PHM', 'Hebrews': 'HEB',
  'James': 'JAS', '1 Peter': '1PE', '2 Peter': '2PE', '1 John': '1JN',
  '2 John': '2JN', '3 John': '3JN', 'Jude': 'JUD', 'Revelation': 'REV',
};

// Chunk size options for Neural2/MP3 mode (multiples of 3)
const CHUNK_SIZE_OPTIONS = [3, 6, 9];

// ── Helper: convert reference to speakable text ───────────────────────────────
function convertReferenceForVoice(reference) {
  if (!reference) return '';
  let r = reference;
  r = r?.replace(/:/g, ' verse ');
  r = r?.replace(/^1\s+/i, 'first ');
  r = r?.replace(/^2\s+/i, 'second ');
  r = r?.replace(/^3\s+/i, 'third ');
  return r;
}

// ── Helper: strip Strong's numbers ───────────────────────────────────────────
function stripStrongsNumbers(text) {
  if (!text) return '';
  return text?.replace(/\{(?:\()?[HG]\d+(?:\))?\}/g, '')?.trim();
}

// ── Helper: strip paragraph markers (¶) and other non-word symbols ───────────
function stripParagraphMarkers(text) {
  if (!text) return '';
  // Remove pilcrow (¶) and similar paragraph/section markers
  return text?.replace(/[¶§]/g, '')?.replace(/\s{2,}/g, ' ')?.trim();
}

// ── Helper: clean verse text (strip Strongs + paragraph markers) ─────────────
function cleanVerseText(text) {
  return stripParagraphMarkers(stripStrongsNumbers(text));
}

// ── Helper: speak text via browser TTS, returns cleanup fn ───────────────────
function speakBrowserTTS(text, { rate = 0.85, volume = 1, gender = 'female' } = {}, onEnd) {
  const synth = window.speechSynthesis;
  if (!synth) { if (onEnd) onEnd(); return () => {}; }

  synth?.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  const voices = synth?.getVoices();
  const preferred = voices?.filter(v => {
    const n = v?.name?.toLowerCase();
    return gender === 'female' ? n?.includes('female') || n?.includes('samantha') || n?.includes('victoria') || n?.includes('zira')
      : n?.includes('male') || n?.includes('david') || n?.includes('mark') || n?.includes('george');
  });
  if (preferred?.[0]) utterance.voice = preferred?.[0];
  else if (voices?.[0]) utterance.voice = voices?.[0];

  utterance.rate = rate;
  utterance.volume = volume;
  utterance.pitch = 1.0;
  utterance.onend = () => { if (onEnd) onEnd(); };
  utterance.onerror = (e) => {
    if (e?.error !== 'interrupted' && e?.error !== 'canceled') { if (onEnd) onEnd(); }
  };

  synth?.speak(utterance);
  return () => synth?.cancel();
}

// ─────────────────────────────────────────────────────────────────────────────

const ProgressiveChunking = ({ verse, onComplete, verseQueue = [], currentVerseIndex = 0, onNextVerse, userId, skipSettings = false, textSizePx = '13px' }) => {
  // ── Context: get the voice the user selected in the audio player ──────────
  const audioCtx = useContext(AudioPlayerContext);
  const playerVoice = audioCtx?.activeVoice || null;
  const playerAvailableVoices = audioCtx?.availableVoices || [];

  // ── Settings state ────────────────────────────────────────────────────────
  const [chunkSize, setChunkSize] = useState(3);       // 3, 6, or 9 words
  const [repetitions, setRepetitions] = useState(3);   // 3–10
  const [voiceGender, setVoiceGender] = useState('female');
  const [volume, setVolume] = useState(1);
  const [speed, setSpeed] = useState(0.8);             // TTS playback speed
  const [autoplay, setAutoplay] = useState(false);
  const [showSettings, setShowSettings] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(userId || null);

  // ── MP3 audio state ───────────────────────────────────────────────────────
  const [mp3Loading, setMp3Loading] = useState(false);
  const [mp3Error, setMp3Error] = useState(null);
  const [mp3Ready, setMp3Ready] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [availableNeural2Voices, setAvailableNeural2Voices] = useState([]);

  // Loaded chapter data
  const mp3AudioRef = useRef(null);       // HTMLAudioElement for MP3 playback
  const allMarksRef = useRef([]);         // all marks for the chapter
  const verseMarksRef = useRef([]);       // verse-start marks only
  const audioDurationRef = useRef(0);
  const segmentCleanupRef = useRef(null); // cleanup fn from playSegment()

  // ── Practice state ────────────────────────────────────────────────────────
  const [chunkSteps, setChunkSteps] = useState([]);    // array of { stopTime, wordCount, isComplete }
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [currentRepetition, setCurrentRepetition] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [isSequentialMode, setIsSequentialMode] = useState(false);

  // Autoplay runner
  const [isAutoplayRunning, setIsAutoplayRunning] = useState(false);
  const isAutoplayRunningRef = useRef(false);
  useEffect(() => { isAutoplayRunningRef.current = isAutoplayRunning; }, [isAutoplayRunning]);

  // Verse start time for the current verse (from marks)
  const verseStartTimeRef = useRef(0);

  // ── Derived ───────────────────────────────────────────────────────────────
  const currentStep = chunkSteps?.[currentStepIndex] || null;
  const isSequential = verseQueue?.length > 1;

  useEffect(() => { setIsSequentialMode(isSequential); }, [isSequential]);

  // ── Load user settings ────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const { data: sessionData } = await authService?.getSession();
        const uid = sessionData?.session?.user?.id;
        if (uid) {
          setCurrentUserId(uid);
          const settings = await userProfileService?.getChunkingSettings(uid);
          if (settings) {
            // Snap saved chunkSize to nearest valid option (3/6/9)
            const saved = settings?.chunkSize || 3;
            const snapped = CHUNK_SIZE_OPTIONS?.reduce((prev, cur) =>
              Math.abs(cur - saved) < Math.abs(prev - saved) ? cur : prev
            );
            setChunkSize(snapped);
            setRepetitions(Math.min(10, Math.max(3, settings?.repetitions || 3)));
            setAutoplay(settings?.autoplay || false);
            setVoiceGender(settings?.voiceGender || 'female');
            setVolume(settings?.volume !== undefined ? settings?.volume : 1);
            setSpeed(settings?.speed !== undefined ? settings?.speed : 0.8);
          }
        }
      } catch (e) {
        console.error('Error loading chunking settings:', e);
      } finally {
        setSettingsLoaded(true);
        // If skipSettings is requested, hide settings screen after loading
        if (skipSettings) {
          setShowSettings(false);
        }
      }
    };
    load();
  }, [skipSettings]);

  // ── Save settings ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!settingsLoaded || !currentUserId) return;
    const t = setTimeout(async () => {
      try {
        await userProfileService?.updateChunkingSettings(currentUserId, {
          chunkSize, repetitions, autoplay, voiceGender, volume, speed,
        });
      } catch (e) { console.error('Error saving chunking settings:', e); }
    }, 1000);
    return () => clearTimeout(t);
  }, [chunkSize, repetitions, autoplay, voiceGender, volume, speed, currentUserId, settingsLoaded]);

  // ── Determine which Neural2 voice to use ─────────────────────────────────
  // Priority: 1) player's active voice if Neural2, 2) gender-matched from available, 3) first available
  const resolveVoice = useCallback((available) => {
    if (!available || available?.length === 0) return null;

    // 1. Use the player's active voice if it's Neural2 and available for this chapter
    if (playerVoice && playerVoice?.includes('Neural2') && available?.includes(playerVoice)) {
      return playerVoice;
    }

    // 2. Match by gender preference
    const genderKey = voiceGender === 'female' ? 'F' : 'J';
    const genderMatch = available?.find(v => v?.includes(`Neural2-${genderKey}`));
    if (genderMatch) return genderMatch;

    // 3. Fallback: first available Neural2
    return available?.[0];
  }, [playerVoice, voiceGender]);

  // ── Build chunk steps from verse text (TTS-only fallback) ─────────────────
  function buildTtsChunkSteps(verseText, chunkSz) {
    if (!verseText) return [];
    const words = cleanVerseText(verseText)?.split(/\s+/)?.filter(Boolean);
    if (words?.length === 0) return [];
    const steps = [];
    let wordCount = 0;
    while (wordCount < words?.length) {
      wordCount = Math.min(wordCount + chunkSz, words?.length);
      steps?.push({
        wordCount,
        stopTime: null,   // no MP3 time — TTS only
        isComplete: wordCount >= words?.length,
        level: Math.ceil(wordCount / chunkSz),
      });
    }
    return steps;
  }

  // ── Load MP3 + marks for the current verse's chapter ─────────────────────
  const loadMp3 = useCallback(async (targetVoice) => {
    if (!verse?.book || !verse?.chapter) return;

    const bookAbbrev = BOOK_ABBREV_MAP?.[verse?.book] || verse?.book;
    setMp3Loading(true);
    setMp3Error(null);
    setMp3Ready(false);

    try {
      // Get available Neural2 voices for this chapter
      const neural2Voices = await getNeural2Voices(bookAbbrev, verse?.chapter);
      setAvailableNeural2Voices(neural2Voices);

      const voiceToUse = targetVoice || resolveVoice(neural2Voices);
      if (!voiceToUse) {
        setMp3Error('No Neural2 audio available for this chapter. Using browser TTS only.');
        setMp3Loading(false);
        return;
      }

      setSelectedVoice(voiceToUse);

      const chapterData = await loadChapterAudio(bookAbbrev, verse?.chapter, voiceToUse);
      if (!chapterData) {
        setMp3Error(`No MP3 found for ${verse?.book} ${verse?.chapter} (${voiceToUse}). Using browser TTS only.`);
        setMp3Loading(false);
        return;
      }

      // Store marks
      allMarksRef.current = chapterData?.allMarks;
      verseMarksRef.current = chapterData?.verseMarks;

      // Create/reset audio element
      if (mp3AudioRef?.current) {
        mp3AudioRef?.current?.pause();
        mp3AudioRef.current.src = '';
      }
      const audioEl = new Audio(chapterData.url);
      audioEl.volume = volume;
      audioEl.preload = 'auto';

      await new Promise((resolve, reject) => {
        audioEl.addEventListener('loadedmetadata', () => {
          audioDurationRef.current = audioEl.duration;
          resolve();
        }, { once: true });
        audioEl.addEventListener('error', reject, { once: true });
        // Timeout fallback
        setTimeout(resolve, 8000);
      });

      mp3AudioRef.current = audioEl;
      setMp3Ready(true);
    } catch (e) {
      console.error('[MP3 Memorization] load error:', e);
      setMp3Error('Failed to load audio. Using browser TTS only.');
    } finally {
      setMp3Loading(false);
    }
  }, [verse, volume, resolveVoice]);

  // Load MP3 when verse changes or settings screen is shown
  useEffect(() => {
    if (verse?.book && verse?.chapter) {
      loadMp3(null);
    }
    return () => {
      // Cleanup on unmount / verse change
      if (segmentCleanupRef?.current) segmentCleanupRef?.current();
      if (mp3AudioRef?.current) {
        mp3AudioRef?.current?.pause();
        mp3AudioRef.current.src = '';
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verse?.book, verse?.chapter]);

  // Keep audio volume in sync
  useEffect(() => {
    if (mp3AudioRef?.current) mp3AudioRef.current.volume = volume;
  }, [volume]);

  // ── Build chunk steps when marks or chunk size changes ────────────────────
  useEffect(() => {
    // If MP3 is ready, build from audio marks
    if (mp3Ready && allMarksRef?.current?.length > 0 && verse?.verse) {
      const verseData = getVerseMarksForVerse(
        allMarksRef?.current,
        verse?.verse,
        audioDurationRef?.current,
        verse?.verseEnd ?? verse?.verse
      );
      if (verseData) {
        verseStartTimeRef.current = verseData?.verseStartTime;
        const steps = buildChunkSteps(
          verseData?.verseStartTime,
          verseData?.chunkMarks,
          verseData?.verseEndTime,
          chunkSize
        );
        setChunkSteps(steps);
        return;
      }
    }
    // TTS-only fallback: build steps from verse text
    if (!mp3Ready && verse?.text) {
      const steps = buildTtsChunkSteps(verse?.text, chunkSize);
      setChunkSteps(steps);
      return;
    }
    setChunkSteps([]);
  }, [verse, chunkSize, mp3Ready]);

  // ── Stop all playback ─────────────────────────────────────────────────────
  const stopAll = useCallback(() => {
    if (segmentCleanupRef?.current) {
      segmentCleanupRef?.current();
      segmentCleanupRef.current = null;
    }
    if (mp3AudioRef?.current) mp3AudioRef?.current?.pause();
    if (window.speechSynthesis) window.speechSynthesis?.cancel();
    setIsPlaying(false);
    setIsAutoplayRunning(false);
  }, []);

  // ── Play one round: TTS reference → audio/TTS segment → (final: TTS reference) ─
  const playOneRound = useCallback((stepIndex, onDone) => {
    if (!verse?.reference) { if (onDone) onDone(); return; }

    const step = chunkSteps?.[stepIndex];
    if (!step) { if (onDone) onDone(); return; }

    const voiceRef = convertReferenceForVoice(verse?.reference);
    const cleanText = cleanVerseText(verse?.text || '');
    const words = cleanText?.split(/\s+/)?.filter(Boolean);
    let wordCount = step?.wordCount || words?.length;
    const partialText = words?.slice(0, wordCount)?.join(' ');
    const isFinal = step?.isComplete;

    setIsPlaying(true);

    // Phase 1: speak opening reference via browser TTS
    const cancelTTS1 = speakBrowserTTS(
      voiceRef,
      { rate: speed, volume, gender: voiceGender },
      () => {
        // Phase 2: play MP3 segment OR TTS verse text
        if (mp3Ready && mp3AudioRef?.current && step?.stopTime !== null) {
          const startTime = verseStartTimeRef?.current;
          const stopTime = step?.stopTime;
          const cleanup = playSegment(
            mp3AudioRef?.current,
            startTime,
            stopTime,
            () => {
              segmentCleanupRef.current = null;
              if (isFinal) {
                // Phase 3 (final only): speak closing reference via TTS
                const cancelTTS2 = speakBrowserTTS(
                  voiceRef,
                  { rate: speed, volume, gender: voiceGender },
                  () => {
                    setIsPlaying(false);
                    if (onDone) onDone();
                  }
                );
                segmentCleanupRef.current = cancelTTS2;
              } else {
                setIsPlaying(false);
                if (onDone) onDone();
              }
            },
            speed
          );
          segmentCleanupRef.current = cleanup;
        } else {
          // TTS-only: speak partial text, then reference if final
          const textToSpeak = isFinal
            ? `${partialText}  ${voiceRef}`
            : partialText;

          const cancelFallback = speakBrowserTTS(
            textToSpeak,
            { rate: speed, volume, gender: voiceGender },
            () => {
              setIsPlaying(false);
              if (onDone) onDone();
            }
          );
          segmentCleanupRef.current = cancelFallback;
        }
      }
    );
    segmentCleanupRef.current = cancelTTS1;
  }, [verse, chunkSteps, mp3Ready, volume, speed, voiceGender]);

  // ── Autoplay runner ───────────────────────────────────────────────────────
  const runAutoplay = useCallback(() => {
    if (!isAutoplayRunningRef?.current) return;

    const totalSteps = chunkSteps?.length;
    if (totalSteps === 0) {
      setIsAutoplayRunning(false);
      return;
    }

    let stepIdx = 0;
    let repIdx = 0;

    const next = () => {
      if (!isAutoplayRunningRef?.current) return;

      if (stepIdx >= totalSteps) {
        setIsAutoplayRunning(false);
        setCurrentStepIndex(totalSteps - 1);
        setCurrentRepetition(repetitions - 1);
        setShowCompletionModal(true);
        return;
      }

      setCurrentStepIndex(stepIdx);
      setCurrentRepetition(repIdx);

      playOneRound(stepIdx, () => {
        if (!isAutoplayRunningRef?.current) return;

        repIdx++;
        if (repIdx >= repetitions) {
          repIdx = 0;
          stepIdx++;
        }

        // Small gap between rounds
        setTimeout(next, 600);
      });
    };

    next();
  }, [chunkSteps, repetitions, playOneRound]);

  // Trigger autoplay runner when isAutoplayRunning flips to true
  const autoplayStartedRef = useRef(false);
  useEffect(() => {
    if (isAutoplayRunning && !autoplayStartedRef?.current) {
      autoplayStartedRef.current = true;
      runAutoplay();
    }
    if (!isAutoplayRunning) {
      autoplayStartedRef.current = false;
    }
  }, [isAutoplayRunning, runAutoplay]);

  // ── Manual controls ───────────────────────────────────────────────────────
  const handleSpeak = useCallback(() => {
    stopAll();
    playOneRound(currentStepIndex, null);
  }, [currentStepIndex, playOneRound, stopAll]);

  const handleNext = useCallback(() => {
    stopAll();
    setIsTransitioning(true);
    setTimeout(() => setIsTransitioning(false), 300);

    if (currentRepetition < repetitions - 1) {
      setCurrentRepetition(r => r + 1);
    } else if (currentStepIndex < chunkSteps?.length - 1) {
      setCurrentStepIndex(i => i + 1);
      setCurrentRepetition(0);
    } else {
      setShowCompletionModal(true);
    }
  }, [currentRepetition, repetitions, currentStepIndex, chunkSteps?.length, stopAll]);

  const handlePrevious = useCallback(() => {
    stopAll();
    setIsTransitioning(true);
    setTimeout(() => setIsTransitioning(false), 300);

    if (currentRepetition > 0) {
      setCurrentRepetition(r => r - 1);
    } else if (currentStepIndex > 0) {
      setCurrentStepIndex(i => i - 1);
      setCurrentRepetition(repetitions - 1);
    }
  }, [currentRepetition, currentStepIndex, repetitions, stopAll]);

  const handleRewindToBeginning = useCallback(() => {
    stopAll();
    setCurrentStepIndex(0);
    setCurrentRepetition(0);
  }, [stopAll]);

  const handleRewindToChunkStart = useCallback(() => {
    stopAll();
    setCurrentRepetition(0);
  }, [stopAll]);

  const handleStartPractice = () => {
    setShowSettings(false);
    setCurrentStepIndex(0);
    setCurrentRepetition(0);
    if (autoplay) {
      setTimeout(() => setIsAutoplayRunning(true), 200);
    }
  };

  const handleComplete = useCallback(() => {
    stopAll();
    if (onComplete) {
      onComplete({
        mode: 'chunking',
        completed: true,
        chunksCompleted: chunkSteps?.length,
        totalRepetitions: chunkSteps?.length * repetitions,
      });
    }
  }, [stopAll, onComplete, chunkSteps?.length, repetitions]);

  const handleRepeatSession = () => {
    stopAll();
    setCurrentStepIndex(0);
    setCurrentRepetition(0);
    setIsAutoplayRunning(false);
  };

  const handleContinueToNext = () => {
    if (isSequentialMode && onNextVerse) {
      onNextVerse();
      setShowCompletionModal(false);
      setCurrentStepIndex(0);
      setCurrentRepetition(0);
      setIsAutoplayRunning(false);
      setShowSettings(false);
      if (autoplay) setTimeout(() => setIsAutoplayRunning(true), 300);
    } else {
      setShowCompletionModal(false);
      if (onComplete) onComplete();
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAll();
      if (mp3AudioRef?.current) {
        mp3AudioRef?.current?.pause();
        mp3AudioRef.current.src = '';
      }
    };
  }, [stopAll]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKey = (e) => {
      if (e?.target?.tagName === 'INPUT' || e?.target?.tagName === 'TEXTAREA' || showSettings || showCompletionModal) return;
      switch (e?.key) {
        case ' ':
          e?.preventDefault();
          if (isPlaying || isAutoplayRunning) stopAll();
          else if (autoplay && !isAutoplayRunning) setIsAutoplayRunning(true);
          else handleSpeak();
          break;
        case 'ArrowLeft':
          e?.preventDefault();
          if (!isAutoplayRunning) handlePrevious();
          break;
        case 'ArrowRight':
          e?.preventDefault();
          if (!isAutoplayRunning) handleNext();
          break;
        default: break;
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isPlaying, isAutoplayRunning, autoplay, showSettings, showCompletionModal, handleSpeak, handlePrevious, handleNext, stopAll]);

  // ── Swipe gesture support for mobile ─────────────────────────────────────
  const { handlers: swipeHandlers } = useTouchSwipe({
    onSwipeLeft: () => {
      if (!isAutoplayRunning && !showSettings && !showCompletionModal) {
        triggerHaptic('light');
        handleNext();
      }
    },
    onSwipeRight: () => {
      if (!isAutoplayRunning && !showSettings && !showCompletionModal) {
        triggerHaptic('light');
        handlePrevious();
      }
    },
    threshold: 60,
  });

  // ── Progress ──────────────────────────────────────────────────────────────
  const totalRounds = chunkSteps?.length * repetitions;
  const completedRounds = currentStepIndex * repetitions + currentRepetition;
  const progress = totalRounds > 0 ? (completedRounds / totalRounds) * 100 : 0;
  const chunkProgress = repetitions > 0 ? ((currentRepetition) / repetitions) * 100 : 0;

  // ── Build display text for current step ───────────────────────────────────
  const buildDisplayText = () => {
    if (!verse?.text || !verse?.reference) return '';
    const cleanText = cleanVerseText(verse?.text);
    const words = cleanText?.split(/\s+/)?.filter(Boolean);
    const step = chunkSteps?.[currentStepIndex];

    // If steps not yet built (audio loading), show reference + first chunk of words
    if (!step) {
      const firstChunkWords = words?.slice(0, chunkSize)?.join(' ');
      return `${verse?.reference}  ${firstChunkWords}`;
    }

    let wordCount = step?.wordCount || words?.length;
    const partial = words?.slice(0, wordCount)?.join(' ');

    if (step?.isComplete) {
      return `${verse?.reference}  ${cleanText}  ${verse?.reference}`;
    }
    return `${verse?.reference}  ${partial}`;
  };

  // ── Settings screen ───────────────────────────────────────────────────────
  if (showSettings) {
    return (
      <div className="bg-card rounded-lg p-6 shadow-warm border border-border">
        <div className="mb-6">
          <h2 className="text-2xl font-heading font-semibold text-card-foreground mb-2">
            Settings
          </h2>
          <p className="text-muted-foreground">
            Configure how the verse will be broken down and repeated
          </p>
        </div>
        {/* Verse Preview */}
        <div className="mb-6 p-4 bg-muted/30 rounded-lg border border-border">
          <p className="text-sm font-medium text-muted-foreground mb-2">Verse to Memorize:</p>
          <p className="text-foreground font-medium mb-1">{verse?.reference}</p>
          <p className="text-muted-foreground text-sm italic">{stripStrongsNumbers(verse?.text)}</p>
        </div>
        {/* Audio Engine Status */}
        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-sm font-medium border ${
          mp3Loading
            ? 'bg-primary/10 border-primary/30 text-primary'
            : mp3Ready
              ? 'bg-success/10 border-success/30 text-success' :'bg-muted/50 border-border text-muted-foreground'
        }`}>
          <Icon name={mp3Loading ? 'Loader' : mp3Ready ? 'Zap' : 'Volume2'} size={16} className={mp3Loading ? 'animate-spin' : ''} />
          {mp3Loading
            ? 'Loading Neural2 audio…'
            : mp3Ready
              ? `Neural2 MP3 ready — ${selectedVoice}`
              : mp3Error || 'No Neural2 audio — browser TTS will be used'}
        </div>
        {/* Voice Settings */}
        <div className="mb-6 p-4 bg-muted/20 border border-border rounded-lg">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Volume2" size={20} color="var(--color-primary)" />
            Voice Settings
          </h3>

          {/* Autoplay Toggle */}
          <div className="mb-4 flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-foreground">Autoplay Full Script</label>
              <p className="text-xs text-muted-foreground mt-0.5">Automatically play all chunks and repetitions</p>
            </div>
            <button
              onClick={() => setAutoplay(!autoplay)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all border-2 ${
                autoplay ? 'bg-primary border-primary' : 'bg-muted border-border'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full transition-transform ${
                autoplay ? 'translate-x-6 bg-white' : 'translate-x-1 bg-muted-foreground'
              }`} />
            </button>
          </div>

          {/* Voice Gender */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">
              Reference Voice Gender
              <span className="ml-1 text-xs text-muted-foreground">(for spoken reference &amp; fallback TTS)</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {['female', 'male']?.map(g => (
                <button
                  key={g}
                  onClick={() => {
                    setVoiceGender(g);
                    if (availableNeural2Voices?.length > 0) {
                      const gKey = g === 'female' ? 'F' : 'J';
                      const match = availableNeural2Voices?.find(v => v?.includes(`Neural2-${gKey}`));
                      if (match) {
                        setSelectedVoice(match);
                        loadMp3(match);
                      }
                    }
                  }}
                  className={`p-3 rounded-lg border-2 transition-all capitalize ${
                    voiceGender === g
                      ? 'border-primary bg-primary/10 text-primary font-semibold' :'border-border bg-muted/30 text-muted-foreground hover:border-primary/50'
                  }`}
                >
                  <Icon name="User" size={18} className="mx-auto mb-1" />
                  {g}
                  {mp3Ready && selectedVoice && (
                    <span className="block text-xs opacity-60 mt-0.5">
                      {g === 'female'
                        ? availableNeural2Voices?.find(v => v?.includes('Neural2-F')) || ''
                        : availableNeural2Voices?.find(v => v?.includes('Neural2-J')) || ''}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Speed */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-2">
              Speed: <span className="text-primary font-semibold">{speed?.toFixed(1)}×</span>
            </label>
            <input
              type="range"
              min="0.5"
              max="1.5"
              step="0.1"
              value={speed}
              onChange={e => setSpeed(parseFloat(e?.target?.value))}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>0.5×</span>
              <span>0.8× (default)</span>
              <span>1.5×</span>
            </div>
          </div>

          {/* Volume */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Volume: <span className="text-primary font-semibold">{Math.round(volume * 100)}%</span>
            </label>
            <input
              type="range" min="0" max="1" step="0.1" value={volume}
              onChange={e => setVolume(parseFloat(e?.target?.value))}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
            />
          </div>
        </div>
        {/* Chunk Size */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-foreground mb-3">
            Chunk Size: <span className="text-primary font-semibold">{chunkSize} words</span>
          </label>
          <div className="grid grid-cols-3 gap-2">
            {CHUNK_SIZE_OPTIONS?.map(sz => (
              <button
                key={sz}
                onClick={() => setChunkSize(sz)}
                className={`p-3 rounded-lg border-2 transition-all text-sm font-medium ${
                  chunkSize === sz
                    ? 'border-primary bg-primary/10 text-primary' :'border-border bg-muted/30 text-muted-foreground hover:border-primary/50'
                }`}
              >
                {sz} words
              </button>
            ))}
          </div>
        </div>
        {/* Repetitions */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-foreground mb-3">
            Repetitions per Chunk: <span className="text-primary font-semibold">{repetitions}×</span>
          </label>
          <input
            type="range" min="3" max="10" value={repetitions}
            onChange={e => setRepetitions(parseInt(e?.target?.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>3×</span>
            <span>10×</span>
          </div>
        </div>
        {/* Preview Info */}
        <div className="mb-6 p-4 bg-muted/20 border border-border rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="Info" size={18} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-foreground mb-1">Practice Preview</p>
              <p className="text-muted-foreground">
                {chunkSteps?.length > 0
                  ? <>This verse will be split into <span className="font-semibold text-foreground">{chunkSteps?.length} chunks</span> of{' '}
                    <span className="font-semibold text-foreground">{chunkSize} words</span> each, repeated{' '}
                    <span className="font-semibold text-foreground">{repetitions}×</span> per chunk.
                    Total rounds: <span className="font-semibold text-foreground">{chunkSteps?.length * repetitions}</span>.</>
                  : mp3Loading
                    ? 'Loading audio marks…'
                    : mp3Error
                      ? 'Audio not available — browser TTS fallback will be used.' :'Chunk preview will appear after audio loads.'}
              </p>
            </div>
          </div>
        </div>
        <Button variant="default" size="lg" fullWidth onClick={handleStartPractice} iconName="Play" iconPosition="left">
          Start Memorization!
        </Button>
      </div>
    );
  }

  // ── Practice screen ───────────────────────────────────────────────────────
  return (
    <div className="bg-card rounded-lg p-6 shadow-warm" {...swipeHandlers}>
      {/* Progress bars */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-muted-foreground">
            Chunk {currentStepIndex + 1} of {chunkSteps?.length}
          </span>
          <span className="text-sm font-medium text-muted-foreground">
            Rep {currentRepetition + 1} of {repetitions}
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-3 mb-2">
          <div className="bg-primary h-3 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
        <div className="w-full bg-muted/50 rounded-full h-1.5">
          <div className="bg-accent h-1.5 rounded-full transition-all duration-300" style={{ width: `${chunkProgress}%` }} />
        </div>
        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-muted-foreground">Overall: {Math.round(progress)}%</p>
          <p className="text-xs text-accent font-medium">Chunk: {Math.round(chunkProgress)}%</p>
        </div>
      </div>
      {/* Engine badge */}
      <div className="mb-3 flex justify-center">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
          mp3Ready
            ? 'bg-success/15 text-success' :'bg-muted text-muted-foreground'
        }`}>
          <Icon name={mp3Ready ? 'Zap' : 'Volume2'} size={11} />
          {mp3Ready ? `Neural2 MP3 · ${selectedVoice}` : 'Browser TTS'}
          {mp3Loading && <span className="ml-1 animate-pulse">…</span>}
        </span>
      </div>
      {/* Current chunk display */}
      <div className={`mb-8 p-8 rounded-lg border-2 transition-all duration-300 transform ${
        isPlaying
          ? 'bg-gradient-to-br from-primary/20 to-accent/20 border-primary shadow-lg scale-105'
          : 'bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20'
      } ${isTransitioning ? 'opacity-50 scale-95' : 'opacity-100 scale-100'}`}>
        <div className="text-center">
          <p className="font-medium text-foreground leading-relaxed transition-all duration-300" style={{ fontSize: textSizePx }}>
            {buildDisplayText()}
          </p>
          {currentStep?.isComplete && (
            <div className="mt-4 inline-flex items-center px-3 py-1 bg-success/20 text-success rounded-full text-sm font-medium animate-bounce">
              <Icon name="CheckCircle" size={16} className="mr-1" />
              Complete Verse
            </div>
          )}
          {isPlaying && (
            <div className="mt-4 inline-flex items-center px-3 py-1 bg-primary/20 text-primary rounded-full text-sm font-medium animate-pulse">
              <Icon name="Volume2" size={16} className="mr-1" />
              {mp3Loading ? 'Loading…' : 'Playing…'}
            </div>
          )}
        </div>
      </div>
      {/* Chunk info */}
      <div className="mb-6 grid grid-cols-3 gap-3">
        <div className="p-3 bg-muted/50 rounded-lg text-center transition-all duration-300 hover:bg-muted">
          <p className="text-xs text-muted-foreground mb-1">Current Level</p>
          <p className="text-lg font-semibold text-foreground">{currentStep?.level || 1}</p>
        </div>
        <div className="p-3 bg-muted/50 rounded-lg text-center transition-all duration-300 hover:bg-muted">
          <p className="text-xs text-muted-foreground mb-1">Words</p>
          <p className="text-lg font-semibold text-foreground">{currentStep?.wordCount}</p>
        </div>
        <div className="p-3 bg-muted/50 rounded-lg text-center transition-all duration-300 hover:bg-muted">
          <p className="text-xs text-muted-foreground mb-1">Remaining</p>
          <p className="text-lg font-semibold text-foreground">
            {(chunkSteps?.length - currentStepIndex - 1) * repetitions + (repetitions - currentRepetition - 1)}
          </p>
        </div>
      </div>
      {/* Playback controls */}
      <div className="mb-4 flex items-center justify-center gap-3">
        <Button
          variant="outline" size="icon"
          onClick={() => {
            if (autoplay && !isAutoplayRunning) setIsAutoplayRunning(true);
            else handleSpeak();
          }}
          disabled={isPlaying || isAutoplayRunning || mp3Loading}
          title={autoplay ? 'Start Full Autoplay' : 'Play Current Chunk'}
        >
          <Icon name="Play" size={20} />
        </Button>

        <Button
          variant="outline" size="icon"
          onClick={stopAll}
          disabled={!isPlaying && !isAutoplayRunning}
          title="Pause"
        >
          <Icon name="Pause" size={20} />
        </Button>

        <Button
          variant="outline" size="icon"
          onClick={stopAll}
          disabled={!isPlaying && !isPlaying && !isAutoplayRunning}
          title="Stop"
        >
          <Icon name="Square" size={20} />
        </Button>
      </div>
      {/* Navigation controls */}
      <div className="flex items-center justify-center gap-3 mb-4">
        <Button
          variant="outline" size="sm"
          onClick={handleRewindToBeginning}
          disabled={currentStepIndex === 0 && currentRepetition === 0}
          iconName="SkipBack" iconPosition="left"
        >
          Beginning
        </Button>

        <Button
          variant="outline" size="sm"
          onClick={handleRewindToChunkStart}
          disabled={currentRepetition === 0}
          iconName="RotateCcw" iconPosition="left"
        >
          Chunk Start
        </Button>

        <Button
          variant="outline" size="icon"
          onClick={handlePrevious}
          disabled={(currentStepIndex === 0 && currentRepetition === 0) || isAutoplayRunning}
          title="Previous"
        >
          <Icon name="ChevronLeft" size={20} />
        </Button>

        <Button
          variant="default" size="lg"
          onClick={handleNext}
          disabled={isAutoplayRunning}
          iconName={currentStepIndex === chunkSteps?.length - 1 && currentRepetition === repetitions - 1 ? 'Check' : 'ChevronRight'}
          iconPosition="right"
          className="px-8"
        >
          {currentStepIndex === chunkSteps?.length - 1 && currentRepetition === repetitions - 1 ? 'Complete' : 'Next'}
        </Button>
      </div>
      {/* Helper text */}
      <div className="text-center mb-4">
        <p className="text-sm text-muted-foreground">
          {isAutoplayRunning ? (
            <span className="flex items-center justify-center text-primary font-medium">
              <Icon name="Play" size={14} className="mr-1" />
              Autoplay running — all chunks playing automatically
            </span>
          ) : autoplay ? (
            <span className="flex items-center justify-center text-primary font-medium">
              <Icon name="Info" size={14} className="mr-1" />
              Autoplay enabled — click Play to start
            </span>
          ) : (
            'Click Play to hear the chunk, or Next to advance manually'
          )}
        </p>
        <p className="text-xs text-muted-foreground mt-2">
          <kbd className="px-2 py-1 bg-muted rounded text-xs">Space</kbd> Play/Stop •{' '}
          <kbd className="px-2 py-1 bg-muted rounded text-xs ml-1">←</kbd> Previous •{' '}
          <kbd className="px-2 py-1 bg-muted rounded text-xs ml-1">→</kbd> Next
          <span className="ml-2 sm:hidden">• Swipe ← Next / → Prev</span>
        </p>
      </div>
      {/* Settings link */}
      <div className="mt-6 pt-6 border-t border-border">
        <Button
          variant="ghost" size="sm" fullWidth
          onClick={() => { stopAll(); setShowSettings(true); }}
          iconName="Settings" iconPosition="left"
        >
          Adjust Settings
        </Button>
      </div>
      <VerseCompletionModal
        verse={verse}
        isOpen={showCompletionModal}
        onClose={() => setShowCompletionModal(false)}
        onRepeat={handleRepeatSession}
        onContinueToNext={isSequentialMode ? handleContinueToNext : null}
        userId={currentUserId}
      />
    </div>
  );
};

export default ProgressiveChunking;